import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api, loadBuildingsForSelector } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import { serviceFeeManagementApi, FeeDetailRow, ServiceFeeImportBatch } from '../../api/serviceFeeManagementApi';
import { downloadExcel, sanitizeExportFilename } from '../../utils/excelExport';
import FeeDetailsImportModal from './FeeDetailsImportModal';
import ImportFileLink from '../../components/common/ImportFileLink';
import toast from 'react-hot-toast';

interface Building {
  id: string;
  name: string;
}

interface FeeDetailsTabProps {
  billingPeriod: string;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('vi-VN').format(Math.round(amount)) + 'đ';

interface DetailTotals {
  openingDebt: number;
  incurredAmount: number;
  surchargeAmount: number;
  taxAmount: number;
  totalReceivable: number;
  paidAmount: number;
  closingDebt: number;
}

const computeDetailTotals = (rows: FeeDetailRow[]): DetailTotals =>
  rows.reduce(
    (acc, row) => ({
      openingDebt: acc.openingDebt + row.openingDebt,
      incurredAmount: acc.incurredAmount + row.incurredAmount,
      surchargeAmount: acc.surchargeAmount + (row.surchargeAmount || 0),
      taxAmount: acc.taxAmount + (row.taxAmount || 0),
      totalReceivable: acc.totalReceivable + row.totalReceivable,
      paidAmount: acc.paidAmount + row.paidAmount,
      closingDebt: acc.closingDebt + row.closingDebt,
    }),
    {
      openingDebt: 0,
      incurredAmount: 0,
      surchargeAmount: 0,
      taxAmount: 0,
      totalReceivable: 0,
      paidAmount: 0,
      closingDebt: 0,
    },
  );

const buildDetailTotalRow = (totals: DetailTotals, rowCount: number): (string | number)[] => [
  `TỔNG CỘNG (${rowCount})`,
  '',
  '',
  totals.openingDebt,
  totals.incurredAmount,
  totals.surchargeAmount,
  totals.taxAmount,
  totals.totalReceivable,
  totals.paidAmount,
  totals.closingDebt,
];

export const FeeDetailsTab: React.FC<FeeDetailsTabProps> = ({ billingPeriod }) => {
  const { user, isSuperAdmin, isOperator } = useAuth();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [buildingId, setBuildingId] = useState('');
  const [feeCategoryId, setFeeCategoryId] = useState('');
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState<Array<{ id: string; name: string; feeCode: string | null }>>([]);
  const [items, setItems] = useState<FeeDetailRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeCategoryTab, setActiveCategoryTab] = useState<string>('all');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importHistory, setImportHistory] = useState<ServiceFeeImportBatch[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deletingBatchId, setDeletingBatchId] = useState<string | null>(null);

  useEffect(() => {
    loadBuildingsForSelector().then((data) => {
      const list = data || [];
      setBuildings(list);
      setBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
    }).catch(console.error);
  }, [user]);

  const loadDetails = async (categoryFilter?: string) => {
    if (!buildingId) {
      toast.error('Vui lòng chọn chung cư');
      return;
    }
    setLoading(true);
    try {
      const catId = categoryFilter && categoryFilter !== 'all' ? categoryFilter : feeCategoryId || undefined;
      const data = await serviceFeeManagementApi.getDetails({
        buildingId,
        billingPeriod,
        feeCategoryId: catId,
        search: search || undefined,
      });
      setCategories(data.categories);
      setItems(data.items);
      setTotal(data.total);
    } catch {
      toast.error('Không thể tải chi tiết phí');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (buildingId) loadDetails(activeCategoryTab);
  }, [activeCategoryTab]);

  const loadImportHistory = async () => {
    if (!buildingId) return;
    setHistoryLoading(true);
    try {
      const history = await serviceFeeManagementApi.getFeeDetailsImportHistory(buildingId, billingPeriod);
      setImportHistory(history);
    } catch {
      toast.error('Không thể tải lịch sử nhập dữ liệu');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (buildingId) {
      loadImportHistory();
    } else {
      setImportHistory([]);
    }
  }, [buildingId, billingPeriod]);

  const handleCategoryTab = (catId: string) => {
    setActiveCategoryTab(catId);
    setFeeCategoryId(catId === 'all' ? '' : catId);
  };

  const columnTotals = useMemo(() => computeDetailTotals(items), [items]);

  const handleExport = () => {
    if (!buildingId) {
      toast.error('Vui lòng chọn chung cư');
      return;
    }
    if (items.length === 0) {
      toast.error('Không có dữ liệu để xuất. Vui lòng tìm kiếm trước.');
      return;
    }

    const buildingName = buildings.find((b) => b.id === buildingId)?.name || buildingId;
    const categoryLabel = activeCategoryTab === 'all'
      ? 'tat-ca'
      : sanitizeExportFilename(categories.find((c) => c.id === activeCategoryTab)?.name || 'loai-phi');
    const headers = [
      'MÃ CĂN HỘ',
      'T. THU PHÍ',
      'LOẠI PHÍ',
      'NỢ ĐẦU KỲ',
      'PHÁT SINH',
      'PHỤ PHÍ',
      'THUẾ',
      'PHẢI THU',
      'ĐÃ THANH TOÁN',
      'NỢ CUỐI KỲ',
    ];
    const dataRows = items.map((row) => [
      row.apartmentCode,
      row.billingPeriod,
      row.feeCategoryName,
      row.openingDebt,
      row.incurredAmount,
      row.surchargeAmount || 0,
      row.taxAmount || 0,
      row.totalReceivable,
      row.paidAmount,
      row.closingDebt,
    ]);
    dataRows.push(buildDetailTotalRow(columnTotals, items.length));

    const filename = `chi-tiet-phi-${sanitizeExportFilename(buildingName)}-${billingPeriod.replace('/', '-')}-${categoryLabel}.xlsx`;
    downloadExcel('Chi tiết phí', headers, dataRows, filename);
    toast.success('Đã xuất file Excel');
  };

  const handleRevertImport = async (batch: ServiceFeeImportBatch) => {
    const confirmed = window.confirm(
      `Xóa dữ liệu đã nhập từ file "${batch.fileName}" (kỳ ${batch.billingPeriod})?\n\n` +
        'Hóa đơn tạo mới sẽ bị xóa; hóa đơn đã cập nhật sẽ được khôi phục về trước khi nhập. ' +
        'Hóa đơn đã thanh toán sẽ được giữ nguyên.',
    );
    if (!confirmed) return;

    setDeletingBatchId(batch.id);
    try {
      const result = await serviceFeeManagementApi.revertFeeDetailsImport(batch.id);
      const parts: string[] = [];
      if (result.deletedCount > 0) parts.push(`xóa ${result.deletedCount} hóa đơn`);
      if (result.restoredCount > 0) parts.push(`khôi phục ${result.restoredCount} hóa đơn`);
      if (result.skippedPaidCount > 0) parts.push(`giữ ${result.skippedPaidCount} đã thanh toán`);
      toast.success(parts.length > 0 ? `Đã ${parts.join(', ')}` : 'Đã đánh dấu xóa lần nhập');
      loadDetails(activeCategoryTab);
      loadImportHistory();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Không thể xóa dữ liệu import');
    } finally {
      setDeletingBatchId(null);
    }
  };

  const canRevertImportBatch = (batch: ServiceFeeImportBatch) =>
    isSuperAdmin ||
    isOperator ||
    (batch.createdById && user?.id === batch.createdById) ||
    (batch.creator?.id && user?.id === batch.creator.id);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {shouldShowBuildingSelector(buildings) && (
        <select
          value={buildingId}
          onChange={(e) => selectBuilding(user?.id, e.target.value, setBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
          className="px-3 py-2 border border-gray-300 rounded-lg min-w-[180px]"
        >
          <option value="">Chọn chung cư</option>
          {buildings.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        )}
        <input
          type="text"
          placeholder="Tìm theo mã căn hộ, tên căn hộ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg min-w-[200px]"
        />
        <button
          onClick={() => loadDetails(activeCategoryTab)}
          disabled={loading}
          className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
        >
          {loading ? 'Đang tải...' : 'Tìm kiếm'}
        </button>
        <button
          onClick={handleExport}
          disabled={loading || items.length === 0}
          className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm disabled:opacity-50"
        >
          Xuất dữ liệu
        </button>
        <button
          onClick={() => {
            if (!buildingId) {
              toast.error('Vui lòng chọn chung cư');
              return;
            }
            if (activeCategoryTab === 'all') {
              toast.error('Chọn tab loại phí (ví dụ Phí vận chuyển rác) trước khi nhập dữ liệu');
              return;
            }
            setShowImportModal(true);
          }}
          disabled={loading || !buildingId}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
        >
          Nhập dữ liệu
        </button>
      </div>

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-200 pb-2">
          <button
            onClick={() => handleCategoryTab('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              activeCategoryTab === 'all'
                ? 'bg-cyan-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Tất cả ({total})
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryTab(cat.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                activeCategoryTab === cat.id
                  ? 'bg-cyan-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-3 text-left font-semibold">MÃ CĂN HỘ</th>
              <th className="px-3 py-3 text-left font-semibold">T. THU PHÍ</th>
              <th className="px-3 py-3 text-left font-semibold">LOẠI PHÍ</th>
              <th className="px-3 py-3 text-right font-semibold">NỢ ĐẦU KỲ</th>
              <th className="px-3 py-3 text-right font-semibold">PHÁT SINH</th>
              <th className="px-3 py-3 text-right font-semibold">PHỤ PHÍ</th>
              <th className="px-3 py-3 text-right font-semibold">THUẾ</th>
              <th className="px-3 py-3 text-right font-semibold">PHẢI THU</th>
              <th className="px-3 py-3 text-right font-semibold">ĐÃ THANH TOÁN</th>
              <th className="px-3 py-3 text-right font-semibold">NỢ CUỐI KỲ</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                  Không có dữ liệu phí dịch vụ
                </td>
              </tr>
            ) : (
              <>
                {items.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <Link
                        to={`/apartments/${row.apartmentId}/details`}
                        className="text-blue-600 hover:underline"
                      >
                        {row.apartmentCode}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{row.billingPeriod}</td>
                    <td className="px-3 py-2">{row.feeCategoryName}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(row.openingDebt)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(row.incurredAmount)}</td>
                    <td className="px-3 py-2 text-right">
                      {row.surchargeAmount ? formatCurrency(row.surchargeAmount) : '-'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {row.taxAmount ? formatCurrency(row.taxAmount) : '-'}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">{formatCurrency(row.totalReceivable)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(row.paidAmount)}</td>
                    <td className="px-3 py-2 text-right text-red-600 font-medium">
                      {formatCurrency(row.closingDebt)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                  <td className="px-3 py-2" colSpan={3}>
                    TỔNG CỘNG ({items.length})
                  </td>
                  <td className="px-3 py-2 text-right">{formatCurrency(columnTotals.openingDebt)}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(columnTotals.incurredAmount)}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(columnTotals.surchargeAmount)}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(columnTotals.taxAmount)}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(columnTotals.totalReceivable)}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(columnTotals.paidAmount)}</td>
                  <td className="px-3 py-2 text-right text-red-600">
                    {formatCurrency(columnTotals.closingDebt)}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Lịch sử nhập dữ liệu</h3>
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-3 text-left font-semibold">Thời gian</th>
                <th className="px-3 py-3 text-left font-semibold">Kỳ thu phí</th>
                <th className="px-3 py-3 text-left font-semibold">Tên file</th>
                <th className="px-3 py-3 text-left font-semibold">Tệp import</th>
                <th className="px-3 py-3 text-right font-semibold">Tổng dòng</th>
                <th className="px-3 py-3 text-right font-semibold">Tạo mới</th>
                <th className="px-3 py-3 text-right font-semibold">Cập nhật</th>
                <th className="px-3 py-3 text-right font-semibold">Bỏ qua</th>
                <th className="px-3 py-3 text-right font-semibold">Lỗi</th>
                <th className="px-3 py-3 text-left font-semibold">Nhân viên</th>
                <th className="px-3 py-3 text-center font-semibold">Xóa</th>
              </tr>
            </thead>
            <tbody>
              {historyLoading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-gray-500">Đang tải...</td>
                </tr>
              ) : importHistory.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-gray-500">Chưa có lịch sử nhập dữ liệu</td>
                </tr>
              ) : (
                importHistory.map((batch) => (
                  <tr key={batch.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2">{new Date(batch.createdAt).toLocaleString('vi-VN')}</td>
                    <td className="px-3 py-2">{batch.billingPeriod}</td>
                    <td className="px-3 py-2">{batch.fileName}</td>
                    <td className="px-3 py-2">
                      <ImportFileLink fileName={batch.fileName} fileUrl={batch.fileUrl} />
                    </td>
                    <td className="px-3 py-2 text-right">{batch.totalRows}</td>
                    <td className="px-3 py-2 text-right text-green-600">{batch.createdCount}</td>
                    <td className="px-3 py-2 text-right text-blue-600">{batch.updatedCount}</td>
                    <td className="px-3 py-2 text-right">{batch.skippedCount}</td>
                    <td className="px-3 py-2 text-right text-red-600">{batch.errorCount}</td>
                    <td className="px-3 py-2">{batch.creator?.name || batch.creator?.email || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      {batch.revertedAt ? (
                        <span
                          className="text-gray-500 text-xs"
                          title={`${batch.reverter?.name || '—'} xóa lúc ${new Date(batch.revertedAt).toLocaleString('vi-VN')} (${batch.revertDeletedCount ?? 0} xóa, ${batch.revertRestoredCount ?? 0} khôi phục)`}
                        >
                          Đã xóa
                        </span>
                      ) : canRevertImportBatch(batch) ? (
                        <button
                          type="button"
                          onClick={() => handleRevertImport(batch)}
                          disabled={deletingBatchId === batch.id}
                          className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                        >
                          {deletingBatchId === batch.id ? 'Đang xóa...' : 'Xóa'}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs" title="Chỉ người nhập hoặc Super Admin/Operator">
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FeeDetailsImportModal
        isOpen={showImportModal}
        buildingId={buildingId}
        buildingName={buildings.find((b) => b.id === buildingId)?.name || ''}
        billingPeriod={billingPeriod}
        feeCategoryId={activeCategoryTab !== 'all' ? activeCategoryTab : undefined}
        feeCategoryName={
          activeCategoryTab !== 'all'
            ? categories.find((c) => c.id === activeCategoryTab)?.name
            : undefined
        }
        onClose={() => setShowImportModal(false)}
        onImported={() => {
          loadDetails(activeCategoryTab);
          loadImportHistory();
        }}
      />
    </div>
  );
};

export default FeeDetailsTab;
